import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, HardHat, Building2, Truck, Droplets, Mountain, Anchor, Construction, TreePine } from "lucide-react";
import { HERO_IMAGE, ABOUT_IMAGE, SERVICES, STATS, CERTIFICATIONS, COMPANY, EXPERTISE } from "../data/site";
import FadeIn from "../components/FadeIn";

const EXPERTISE_ICONS = { Droplets, Mountain, Anchor, Construction, TreePine, Truck };

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[92vh] flex items-end bg-[#0B1E3F] text-white overflow-hidden"
      >
        <img
          src={HERO_IMAGE}
          alt="Cantiere Fratelli D'Ambrosio"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1E3F]/60 via-[#0B1E3F]/30 to-[#0B1E3F]" />
        <div className="grain" />

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 pt-32 pb-20 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="inline-block w-8 h-[2px] bg-[#60A5FA]" />
            <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#60A5FA]">
              Impresa Edile · dal 1970
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="hero-title text-white text-[clamp(3rem,9vw,9rem)] max-w-[1200px]"
          >
            Esperienza<br />
            e passione <span className="text-[#1D4ED8]">per costruire</span><br />
            il futuro.
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center"
          >
            <Link
              to="/servizi"
              data-testid="hero-cta-servizi"
              className="inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#1E3A8A] text-white px-7 py-4 rounded-full font-display font-bold text-sm tracking-wider uppercase transition-colors"
            >
              Scopri i servizi
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contatti"
              data-testid="hero-cta-contatti"
              className="inline-flex items-center gap-2 border border-white/40 hover:bg-white hover:text-[#0B1E3F] text-white px-7 py-4 rounded-full font-display font-bold text-sm tracking-wider uppercase transition-colors"
            >
              Richiedi preventivo
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-0 border-t border-white/10"
          >
            {STATS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + i * 0.1 }}
                data-testid={`hero-stat-${i}`}
                className={`py-6 md:py-8 md:px-8 ${i !== 0 ? "md:border-l border-white/10" : ""}`}
              >
                <div className="font-display font-black text-3xl md:text-4xl tracking-tighter text-[#60A5FA]">
                  {s.value}
                </div>
                <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-400 mt-2">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section data-reveal className="py-24 md:py-32 bg-[#F5F5F3] border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline mb-6">/ 01 — Chi siamo</div>
            <h2 className="hero-title text-4xl md:text-5xl lg:text-6xl">
              Mezzo secolo<br />di cantieri.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-8 space-y-6 text-lg text-neutral-700 leading-relaxed">
            <p>
              Qui comincia il nostro progetto, basato sull'esperienza passata e presente per costruire il
              futuro. La nostra azienda opera da svariati anni nel settore delle costruzioni e da più di
              mezzo secolo si avvale dell'esperienza e passione del suo titolare.
            </p>
            <p>
              Un fattore tramandato ai propri figli — parte integrante e solida dell'attuale struttura
              organizzativa dell'impresa. Operiamo su scala locale, costituendo un punto di riferimento
              per le aziende del settore, distinguendoci per versatilità dei servizi e disponibilità di
              risorse e mezzi specializzati.
            </p>
            <Link
              to="/azienda"
              data-testid="home-about-link"
              className="inline-flex items-center gap-2 font-display font-bold text-sm tracking-wider uppercase border-b-2 border-[#0B1E3F] pb-1 hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors"
            >
              Scopri l'azienda <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-reveal className="py-24 md:py-32 bg-white border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="overline mb-6">/ 02 — Settori</div>
              <h2 className="hero-title text-4xl md:text-5xl lg:text-6xl max-w-2xl">
                Quattro settori.<br />Un'unica competenza.
              </h2>
            </div>
            <Link
              to="/servizi"
              className="inline-flex items-center gap-2 font-display font-bold text-sm tracking-wider uppercase border-b-2 border-[#0B1E3F] pb-1 hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors self-start"
            >
              Tutti i servizi <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div data-reveal-stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/servizi/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="service-card group relative bg-[#0B1E3F] p-8 md:p-10 aspect-[4/5] flex flex-col justify-between overflow-hidden rounded-3xl"
              >
                <div className="absolute inset-0 z-0">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1E3F] via-[#0B1E3F]/60 to-[#0B1E3F]/20 group-hover:from-[#0B1E3F] group-hover:via-[#0B1E3F]/40 transition-colors duration-500" />
                </div>

                <div className="relative z-10">
                  <div className="font-mono text-xs tracking-[0.25em] text-white/80 transition-colors">
                    / {s.number}
                  </div>
                </div>

                <div className="relative z-10 mt-auto">
                  <h3 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl mb-3 text-white transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-white/80 transition-colors max-w-xs">
                    {s.short}
                  </p>
                  <div className="mt-8 inline-flex items-center gap-2 font-display font-bold text-xs tracking-wider uppercase text-[#60A5FA] group-hover:text-white transition-colors">
                    Approfondisci <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERTISE — ambiti di approfondimento */}
      <section data-reveal className="py-24 md:py-32 bg-[#F5F5F3] border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <div className="overline mb-6">/ 03 — Specializzazioni</div>
              <h2 className="hero-title text-4xl md:text-5xl lg:text-6xl">
                Gli impieghi che<br />
                <span className="text-[#1D4ED8]">approfondiamo</span><br />
                di più.
              </h2>
            </div>
            <div className="md:col-span-7 md:pt-8 text-lg text-neutral-700 leading-relaxed">
              <p>
                Sei ambiti in cui abbiamo maturato un'esperienza specifica nel corso degli anni.
                Dalle opere strutturali con micropali al consolidamento dei versanti, dall'ingegneria
                naturalistica alle opere idrauliche e stradali — supportati da un parco mezzi completo
                pronto per ogni situazione.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {EXPERTISE.map((e, i) => {
              const Icon = EXPERTISE_ICONS[e.icon];
              const isHighlight = e.highlight;
              return (
                <div
                  key={i}
                  data-testid={`expertise-card-${i}`}
                  className={`p-8 md:p-10 group transition-colors ${
                    isHighlight
                      ? "bg-[#0B1E3F] text-white hover:bg-[#1D4ED8]"
                      : "bg-white hover:bg-[#1D4ED8] hover:text-white"
                  }`}
                >
                  <div className="flex items-start justify-between mb-12">
                    <div
                      className={`w-14 h-14 border flex items-center justify-center transition-colors ${
                        isHighlight
                          ? "border-[#60A5FA] text-[#60A5FA] group-hover:bg-white group-hover:text-[#1D4ED8] group-hover:border-white"
                          : "border-[#1D4ED8] text-[#1D4ED8] group-hover:bg-white group-hover:text-[#1D4ED8] group-hover:border-white"
                      }`}
                    >
                      <Icon className="w-6 h-6" strokeWidth={1.75} />
                    </div>
                    <div
                      className={`font-mono text-[11px] tracking-[0.25em] uppercase ${
                        isHighlight ? "text-white/50" : "text-neutral-400 group-hover:text-white/70"
                      }`}
                    >
                      / 0{i + 1}
                    </div>
                  </div>
                  <h3 className="font-display font-black uppercase tracking-tighter text-2xl md:text-3xl mb-3">
                    {e.title}
                  </h3>
                  <p
                    className={`text-base leading-relaxed ${
                      isHighlight ? "text-white/80" : "text-neutral-600 group-hover:text-white/90"
                    }`}
                  >
                    {e.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SPLIT ABOUT */}
      <section data-reveal className="bg-[#0B1E3F] text-white relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 relative">
          <div className="relative min-h-[400px] md:min-h-[600px]">
            <img src={ABOUT_IMAGE} alt="Mezzi e attrezzature" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="px-6 md:px-16 py-20 md:py-28">
            <div className="overline text-[#60A5FA] mb-6">/ 04 — Il valore</div>
            <h2 className="hero-title text-white text-4xl md:text-5xl lg:text-6xl mb-10">
              Dalla fondazione<br />alla consegna.
            </h2>
            <div className="space-y-6">
              {[
                { icon: HardHat, title: "Maestranze specializzate", desc: "Operai e artigiani formati, con esperienza maturata sul campo in decenni di attività." },
                { icon: Truck, title: "Mezzi e attrezzature", desc: "Parco mezzi completo per affrontare qualunque tipologia di intervento in autonomia." },
                { icon: ShieldCheck, title: "Qualità certificata", desc: "Certificazioni SOA e ISO 9001 a garanzia dei processi e dei risultati." },
                { icon: Building2, title: "Versatilità operativa", desc: "Dal cantiere civile all'opera stradale fino agli interventi specialistici." },
              ].map((v, i) => (
                <div key={i} className="flex gap-5 border-t border-white/10 pt-6">
                  <div className="w-12 h-12 border border-[#1D4ED8] text-[#60A5FA] flex-shrink-0 flex items-center justify-center rounded-xl">
                    <v.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-display font-bold text-lg uppercase tracking-tight mb-1">{v.title}</h4>
                    <p className="text-neutral-400 text-sm leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS STRIP */}
      <section data-reveal className="py-20 md:py-28 bg-[#F5F5F3] border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="overline mb-6">/ 05 — Certificazioni</div>
              <h2 className="hero-title text-4xl md:text-5xl lg:text-6xl max-w-2xl">
                Qualificati.<br />Certificati.
              </h2>
            </div>
            <Link
              to="/certificazioni"
              className="inline-flex items-center gap-2 font-display font-bold text-sm tracking-wider uppercase border-b-2 border-[#0B1E3F] pb-1 hover:border-[#1D4ED8] hover:text-[#1D4ED8] transition-colors self-start"
            >
              Tutti i documenti <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div data-reveal-stagger className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {CERTIFICATIONS.map((c, i) => (
              <div
                key={i}
                data-testid={`home-cert-${i}`}
                className="bg-white p-8 md:p-10 hover:bg-[#0B1E3F] rounded-2xl border border-neutral-200 hover:text-white transition-colors group"
              >
                <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#1D4ED8] mb-8">
                  / 0{i + 1}
                </div>
                <div className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight uppercase mb-4 break-words">
                  {c.code}
                </div>
                <div className="text-sm text-neutral-600 group-hover:text-neutral-300 leading-relaxed">
                  {c.title}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-reveal className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
          <div className="overline mb-8">/ Iniziamo a lavorare</div>
          <h2 className="hero-title text-5xl md:text-7xl lg:text-8xl max-w-5xl mx-auto">
            Il vostro prossimo<br />
            <span className="text-[#1D4ED8]">cantiere</span> inizia qui.
          </h2>
          <p className="mt-8 text-lg text-neutral-600 max-w-xl mx-auto">
            Richiedete un sopralluogo o un preventivo. Vi risponderemo entro 24 ore lavorative con una
            proposta chiara e dettagliata.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contatti"
              data-testid="home-cta-contatti"
              className="inline-flex items-center gap-2 bg-[#0B1E3F] hover:bg-[#1D4ED8] text-white px-8 py-4 rounded-full font-display font-bold text-sm tracking-wider uppercase transition-colors"
            >
              Contattaci ora <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="home-cta-phone"
              className="inline-flex items-center gap-2 border border-[#0B1E3F] hover:bg-[#0B1E3F] hover:text-white text-[#0B1E3F] px-8 py-4 rounded-full font-display font-bold text-sm tracking-wider uppercase transition-colors"
            >
              Chiama {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
